import { Location } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  Brand,
  BrandRow,
  ConfigDashboardInstance,
  CurrentStatus,
  DisplayDashboard,
  Filter,
  FilterOption,
  FilterValue
} from '../types';
import { CurrencySymbol } from '../types/currency-format';
import { CurrencyService } from './currency.service';
import { SelectionService } from './selection.service';
import { UserModel } from '@portal/app/shared/models/userModel';
import { BehaviorSubject, take } from 'rxjs';
import { DashboardService } from '@portal/app/shared/services/dashboard.service';
import { compact, isArray, isEmpty } from 'lodash-es';
import {
  getDefaultDashboard,
  findRouterLinkByLiteralId,
  shouldLandOnAdminSettings
} from '@portal/app/dashboard/utils';
import { FeatureConfigService } from '@portal/app/shared/services/feature-config.service';
import { ConfigDashboardService } from './config-dashboard.service';
import { NavigationService } from './navigation.service';
import { MmmExportBannerService } from '../components/mmm-export-banner/mmm-export-banner.service';
import { NavbarConstants } from '@portal/app/shared/types/navigation';

interface InteractionData {
  key: string;
  subType: string;
  isHovered: boolean;
}

type BulkModeValue = 'day' | 'week' | 'month';

/** Everything a retention entry needs to gate access and build its target URL. */
interface RetainContext {
  dashboards: DisplayDashboard[];
  configDashboards: Record<string, ConfigDashboardInstance>;
  user: UserModel;
  slug: string;
}

/**
 * A retainable location for brand-switch. Each entry owns its own URL
 * recognition, access gate, and target URL, so adding a new route to the
 * "retain last location on brand switch" behavior is a single entry here
 * rather than another branch in handleRedirect/extractLiteralIdFromUrl.
 */
interface RetainEntry {
  matches(url: string): boolean;
  canAccess(ctx: RetainContext): boolean;
  buildUrl(ctx: RetainContext): string;
  extraQueryParams?(ctx: RetainContext): Record<string, unknown> | undefined;
}

// Special-cased routes that aren't recoverable via the generic dashboards /
// settings-menu lookup (they live outside the dashboards list or have deep
// sub-routes the generic URL parser can't canonicalize). Standard dashboards
// (home, xcd, benchmarks, admin settings) fall through to the generic path.
const RETAIN_REGISTRY: RetainEntry[] = [
  {
    // geo-designer-v2 isn't registered in staticMeasuredDashboards — it's
    // surfaced via nav based on the "Geo Tests" config dashboard, so the
    // standard lookup can't route to it. Retain only if that config is visible.
    matches: (url) => /\/products\/geo-designer-v2(\/|$|\?)/.test(url),
    canAccess: (ctx) =>
      ConfigDashboardService.isInstanceVisible(
        ctx.configDashboards[NavbarConstants.geoDesignerId],
        ctx.user
      ),
    buildUrl: (ctx) => `/a/${ctx.slug}/products/geo-designer-v2/home`
  },
  {
    // MPO isn't registered in staticMeasuredDashboards; gate on whether the
    // switched-to brand has an MPO product dashboard. /products/mpo's empty
    // child route redirects to the recommended plan.
    matches: (url) => /\/products\/mpo(\/|$|\?)/.test(url),
    canAccess: (ctx) =>
      ctx.dashboards.some(
        (dashboard) =>
          dashboard.productId === NavbarConstants.mpoV2ProductId &&
          dashboard.status !== CurrentStatus.inactive
      ),
    buildUrl: (ctx) => `/a/${ctx.slug}/products/mpo`
  },
  {
    // MIM is surfaced via a config dashboard (not in the dashboards list). Gate
    // retention on the same visibility rules the nav uses (ACTIVE -> everyone,
    // DRAFT -> internal only) so a brand switch never lands a user on a MIM
    // page the nav keeps hidden — the route itself has no guard.
    matches: (url) => /\/products\/mim-page(\/|$|\?)/.test(url),
    canAccess: (ctx) =>
      ConfigDashboardService.isInstanceVisible(
        ctx.configDashboards[NavbarConstants.mimPageId],
        ctx.user
      ),
    buildUrl: (ctx) => `/a/${ctx.slug}/products/mim-page`
  },
  {
    matches: (url) => /\/products\/notification-center(\/|$|\?)/.test(url),
    canAccess: (ctx) =>
      ConfigDashboardService.isInstanceVisible(
        ctx.configDashboards[NavbarConstants.notificationCenterId],
        ctx.user
      ),
    buildUrl: (ctx) => `/a/${ctx.slug}/products/notification-center`
  }
];

@Injectable({
  providedIn: 'root'
})
export class ViewService {
  private interactionSubject = new BehaviorSubject<InteractionData | null>(
    null
  );

  private selectedBulkMode = new BehaviorSubject<BulkModeValue>('week');
  public selectedBulkMode$ = this.selectedBulkMode.asObservable();
  public interactionEvent$ = this.interactionSubject.asObservable();

  constructor(
    private router: Router,
    private readonly selectionService: SelectionService,
    private readonly dashboardService: DashboardService,
    private currencyService: CurrencyService,
    private location: Location,
    private readonly featureConfig: FeatureConfigService,
    private readonly configDashboardService: ConfigDashboardService,
    private injector: Injector,
    private readonly mmmExportBannerService: MmmExportBannerService
  ) {}

  private get navigationService(): NavigationService {
    return this.injector.get(NavigationService);
  }

  switchBrand(row: BrandRow, user: UserModel): void {
    if (row.status === CurrentStatus.active && user) {
      // Set client/brand selection to be able to access it later.
      this.selectionService.setSelection(row.client, row.brand);
      this.featureConfig.fetchData();
      this.dashboardService
        .getDashboards(this.selectionService.getSelection())
        .pipe(take(1))
        .subscribe({
          next: (dashboards) => {
            this.handleRedirect(dashboards, row, user as UserModel);
          },
          error: () => {
            // On failure, reload the page to ensure brand label and data stay in sync
            window.location.reload();
          }
        });
    }
  }

  public handleRedirect(
    dashboards: DisplayDashboard[] = [],
    row: BrandRow,
    user: UserModel
  ) {
    this.configDashboardService
      .getConfigDashboardsByLiteralId()
      .then((configDashboards) => {
        const ctx: RetainContext = {
          dashboards,
          configDashboards,
          user,
          slug: this.selectionService.buildSelectionSlug()
        };

        const entry = RETAIN_REGISTRY.find((e) =>
          e.matches(this.location.path())
        );

        if (entry) {
          if (entry.canAccess(ctx)) {
            this.setUpBrandCurrency(row.brand);
            this.redirectTo(entry.buildUrl(ctx), entry.extraQueryParams?.(ctx));
          } else {
            // Matched a known route but the brand can't access it — fall back
            // to the brand's default dashboard rather than the generic lookup.
            this.redirectToDashboard(dashboards, row, user, configDashboards);
          }
        } else {
          this.redirectToGenericLocation(
            dashboards,
            row,
            user,
            configDashboards
          );
        }
      })
      .catch(() => {
        // Config lookup failed — don't re-hit the same endpoint via
        // redirectToDashboard. Land on the brand's default dashboard using the
        // already-loaded dashboards so a brand switch never strands the user.
        this.setUpBrandCurrency(row.brand);
        const defaultDashboard = getDefaultDashboard(dashboards, user);
        this.redirectTo(
          `a/${this.selectionService.buildSelectionSlug()}/products/${defaultDashboard?.routerLinkPart}`
        );
      })
      .finally(() => {
        this.mmmExportBannerService.resetSignals();
      });
  }

  // Standard dashboards (home, xcd, benchmarks) and settings routes are
  // recovered by matching the current URL's literalId against the brand's
  // dashboards list and settings menu; otherwise land on a default dashboard.
  private redirectToGenericLocation(
    dashboards: DisplayDashboard[],
    row: BrandRow,
    user: UserModel,
    configDashboards: Record<string, ConfigDashboardInstance>
  ): void {
    const searchForLiteralId = this.extractLiteralIdFromUrl();

    if (!searchForLiteralId) {
      this.redirectToDashboard(dashboards, row, user, configDashboards);
      return;
    }

    const hasAccessToRouterLink = findRouterLinkByLiteralId(
      dashboards,
      searchForLiteralId
    );

    if (hasAccessToRouterLink) {
      this.setUpBrandCurrency(row.brand);
      this.redirectToReferrer(hasAccessToRouterLink);
      return;
    }

    const settingsMenu = this.navigationService.createSettingsMenuItems(
      configDashboards,
      'products'
    );

    const settingMenuItemWithAccess = settingsMenu.find(
      (menuItem) =>
        searchForLiteralId === this.extractLiteralIdFromUrl(menuItem.routerLink)
    );

    if (settingMenuItemWithAccess) {
      this.setUpBrandCurrency(row.brand);
      this.redirectTo(settingMenuItemWithAccess.routerLink as string);
    } else {
      this.redirectToDashboard(dashboards, row, user, configDashboards);
    }
  }

  private extractLiteralIdFromUrl(
    currentUrl = this.location.path()
  ): string | null {
    const urlRegex = /\/products\/(?:[^/]+\/)?([^/?]+)/;
    const matchUrl = currentUrl.match(urlRegex);

    if (matchUrl) {
      let literalId = matchUrl[1];

      // Handle use cases specific to Geo
      switch (literalId) {
        case 'geoExperiments': //geo-doe/geoExperimentes
          literalId = 'geo-doe';
          break;
        case 'home': //geo-designer/home
          literalId = 'geo-designer';
          break;
        case 'admin':
          literalId = 'integration';
          break;
      }

      if (literalId) {
        return literalId;
      }
    }

    return null;
  }

  private redirectToReferrer(routerLink: {
    routerLinkPart: string;
    isMultiView: boolean;
  }): void {
    //TO DO: Remove when Optimization Report url is updated
    const modifyOptReportUrl = (part: string): string => {
      if (part === 'portfolio/optimization-report') {
        return part.replace('portfolio/', '');
      }
      return part;
    };

    const routerLinkPart = routerLink.routerLinkPart;
    const modifiedRouterLink = modifyOptReportUrl(routerLinkPart);

    const navigationExtraQueryParams = routerLink.isMultiView
      ? {
          isMultiView: routerLink.isMultiView
        }
      : undefined;

    this.redirectTo(
      `/a/${this.selectionService.buildSelectionSlug()}/products/${modifiedRouterLink}`,
      navigationExtraQueryParams
    );
  }

  public redirectToDashboard(
    dashboards: DisplayDashboard[] = [],
    row: BrandRow,
    user: UserModel,
    configDashboards?: Record<string, ConfigDashboardInstance>
  ) {
    this.setUpBrandCurrency(row.brand);
    const selectionSlug = this.selectionService.buildSelectionSlug();

    // Reuse an already-fetched config map when the caller has one, so a brand
    // switch doesn't hit getConfigDashboardsByLiteralId twice.
    const configDashboards$ = configDashboards
      ? Promise.resolve(configDashboards)
      : this.configDashboardService.getConfigDashboardsByLiteralId();

    configDashboards$.then((resolvedConfigDashboards) => {
      if (
        shouldLandOnAdminSettings(dashboards, user, resolvedConfigDashboards)
      ) {
        this.redirectTo(`a/${selectionSlug}/products/admin`);
      } else {
        const defaultDashboard = getDefaultDashboard(dashboards, user);
        this.redirectTo(
          `a/${selectionSlug}/products/${defaultDashboard?.routerLinkPart}`
        );
      }
    });
  }

  public redirectTo(uri: string, extraQueryParams = {}): void {
    this.router.navigate(['/login'], {
      skipLocationChange: true,
      queryParams: { returnUrl: uri, ...extraQueryParams }
    });
  }

  public setUpBrandCurrency(brand: Brand) {
    const currencyCode = brand.currencyCode ? brand.currencyCode : 'USD';
    const currencyDisplay: CurrencySymbol =
      this.currencyService.checkCurrencySymbol(brand);
    const currencyLocale = brand?.currencyLocale
      ? brand.currencyLocale
      : 'en-US';
    this.currencyService.updateCurrencyData({
      code: currencyCode,
      locale: currencyLocale,
      display: currencyDisplay
    });
  }

  setComponentInteraction(value: InteractionData): void {
    this.interactionSubject.next(value);
  }

  updateFilterValues(
    filters: Filter[] = [],
    chosenFilters: Filter[]
  ): Filter[] {
    return filters.map((filter) => {
      const f = chosenFilters.find((ff) => ff.literalId === filter.literalId);
      if (f && !f.literalId.startsWith('child')) {
        filter.value = f.value;
        filter.value = this.setupFirstOptionValue(filter);
      }
      return filter;
    });
  }

  setupFirstOptionValue(filter: Filter): FilterValue {
    if (
      isArray(filter.options) &&
      !isEmpty(compact(filter.options)) &&
      filter.options[0] &&
      typeof filter.options?.[0] === 'object' &&
      filter.value !== null &&
      !isEmpty(filter.value)
    ) {
      if (
        filter.type !== 'string[]' &&
        !(filter.options as unknown as FilterOption[])
          .map(({ value }) => value)
          .includes(filter.value as string)
      ) {
        filter.value = (filter.options as unknown as FilterOption[])[0]
          ?.value as FilterValue;
      }
    }
    return filter.value;
  }
}
